import { useEffect, useRef, useState, type DragEvent, type ClipboardEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  useChat,
  useChatMessages,
  useChatAssets,
  useChatActiveJobs,
  useChatCostTotal,
  useChatLastFailedBatch,
  useRetryFailedBatch,
  type FailedBatch,
} from '../hooks/useApi';
import { useChatUpload, type PendingAttachment } from '../hooks/useChatUpload';
import { BASE } from '../api/client';
import { streamPostSse, subscribeGetSse } from '../api/sse';
import { ChatHeader } from './ChatHeader';
import { Restyle360Panel } from './Restyle360Panel';
import { RenderJobCard } from './RenderJobCard';
import type { Asset, Message } from '../api/types';
import { useComposerBridge } from '../stores/composerBridge';

interface ChatPanelProps {
  chatId: string;
}

interface PendingAssistantMessage {
  text: string;
  toolUses: { id: string; name: string; input: unknown; result?: unknown }[];
}

export function ChatPanel({ chatId }: ChatPanelProps) {
  const { data: chat } = useChat(chatId);
  const { data: messages, refetch } = useChatMessages(chatId);
  const { data: chatAssets } = useChatAssets(chatId);
  const { data: persistedCost, refetch: refetchCost } = useChatCostTotal(chatId);
  const { data: activeJobs, refetch: refetchActiveJobs } = useChatActiveJobs(chatId);
  const { data: failedBatch, refetch: refetchFailedBatch } = useChatLastFailedBatch(chatId);
  const retryBatch = useRetryFailedBatch();
  const upload = useChatUpload(chatId);
  const qc = useQueryClient();
  const [draft, setDraft] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [pending, setPending] = useState<PendingAssistantMessage | null>(null);
  // Costo acumulado del turno en streaming. Se suma al persistido del backend
  // (que viene de chatTotal) para mostrar el total del chat en el header.
  const [streamingCost, setStreamingCost] = useState(0);
  const totalCost = (persistedCost?.total ?? 0) + streamingCost;
  const [dragOver, setDragOver] = useState(false);
  const [mention, setMention] = useState<{ query: string; cursor: number } | null>(null);
  const [extraAssetIds, setExtraAssetIds] = useState<string[]>([]);
  const [restyleOpen, setRestyleOpen] = useState(false);
  const bridgePending = useComposerBridge((s) => s.pending);
  const clearBridge = useComposerBridge((s) => s.clear);
  const scrollRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const dragClearTimer = useRef<number | null>(null); // auto-oculta el overlay si dejan de llegar dragover (drop, salir de la ventana, cancelar)

  // Auto-scroll al final cuando llegan mensajes nuevos
  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, pending]);

  // SSE async para eventos del chat (jobs completados / fallidos / mensajes nuevos)
  useEffect(() => {
    const unsub = subscribeGetSse(`/chats/${chatId}/events`, (evt) => {
      if (
        evt.type === 'job.completed' ||
        evt.type === 'job.failed' ||
        evt.type === 'message.created'
      ) {
        void refetch();
        void refetchActiveJobs();
        void refetchFailedBatch();
        void refetchCost();
        // Refrescar también la lista de assets del chat (las refs en el panel
        // lateral) y los fal calls del Prompts modal.
        qc.invalidateQueries({ queryKey: ['chat', chatId, 'assets'] });
        qc.invalidateQueries({ queryKey: ['instance-fal-calls'] });
      }
    });
    return unsub;
  }, [chatId, refetch, refetchActiveJobs, refetchFailedBatch, refetchCost, qc]);

  // ---- Drag & drop sobre toda la zona del chat ----
  // Mostramos el overlay mientras `dragover` siga llegando y lo auto-ocultamos
  // cuando deja de llegar (al soltar, salir de la ventana o cancelar con Esc).
  // Evita el contador enter/leave, que se desincronizaba y dejaba el overlay pegado.
  const keepDragOverlay = () => {
    setDragOver(true);
    if (dragClearTimer.current) window.clearTimeout(dragClearTimer.current);
    dragClearTimer.current = window.setTimeout(() => setDragOver(false), 400);
  };
  const onDragEnter = (e: DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    keepDragOverlay();
  };
  const onDragOver = (e: DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
    keepDragOverlay();
  };
  const onDrop = (e: DragEvent<HTMLDivElement>) => {
    if (!e.dataTransfer.types.includes('Files')) return;
    e.preventDefault();
    if (dragClearTimer.current) window.clearTimeout(dragClearTimer.current);
    setDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) void upload.addFiles(files);
  };

  // ---- Paste de imágenes desde el clipboard ----
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const items = Array.from(e.clipboardData.items);
    const fileItems = items.filter((it) => it.kind === 'file');
    if (fileItems.length === 0) return; // dejar que el paste de texto funcione normal
    e.preventDefault();
    const files = fileItems
      .map((it) => it.getAsFile())
      .filter((f): f is File => f !== null);
    if (files.length > 0) void upload.addFiles(files);
  };

  const onFilePicker = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (files.length > 0) void upload.addFiles(files);
    e.target.value = ''; // reset para permitir re-elegir mismo archivo
  };

  // ---- Mention detection (autocomplete con #) ----
  const onTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    const cursor = e.target.selectionStart;
    setDraft(value);

    // Buscar # más cercano antes del cursor (sin espacio en medio)
    const before = value.slice(0, cursor);
    const match = /#([\w-]*)$/.exec(before);
    if (match) {
      setMention({ query: match[1], cursor });
    } else {
      setMention(null);
    }
  };

  const insertMention = (asset: Asset) => {
    if (!textareaRef.current || !mention) return;
    const handle = asset.metadata?.shortRef || asset.id.slice(0, 8);
    const before = draft.slice(0, mention.cursor);
    const after = draft.slice(mention.cursor);
    const replaced = before.replace(/#[\w-]*$/, `#${handle} `);
    setDraft(replaced + after);
    setExtraAssetIds((prev) => (prev.includes(asset.id) ? prev : [...prev, asset.id]));
    setMention(null);
    setTimeout(() => {
      textareaRef.current?.focus();
      const pos = replaced.length;
      textareaRef.current?.setSelectionRange(pos, pos);
    }, 0);
  };

  const filteredAssets = (chatAssets ?? []).filter((a) => {
    if (!mention) return false;
    const q = mention.query.toLowerCase();
    if (!q) return true;
    const ref = a.metadata?.shortRef?.toLowerCase() ?? '';
    const desc = a.metadata?.description?.toLowerCase() ?? '';
    return ref.includes(q) || desc.includes(q);
  });

  const [lastError, setLastError] = useState<string | null>(null);
  // Render optimista del user msg que acabamos de mandar — el backend lo
  // persiste inmediatamente, pero `messages` solo se refresca al terminar
  // el stream. Sin esto, durante el turno se ve "el agente respondiendo"
  // sin haberse visto antes lo que el operador escribió.
  const [pendingUser, setPendingUser] = useState<{ text: string; attachmentsCount: number; createdAt: string } | null>(
    null,
  );

  const runStream = async (path: string, body: unknown) => {
    setStreaming(true);
    setMention(null);
    setPending({ text: '', toolUses: [] });
    setLastError(null);
    try {
      await streamPostSse(path, body, (evt) => {
        if (evt.type === 'text_delta') {
          const data = evt.data as { text: string };
          setPending((p) => (p ? { ...p, text: p.text + data.text } : p));
        } else if (evt.type === 'tool_use') {
          const data = evt.data as { tool: string; input: unknown; id: string };
          setPending((p) =>
            p
              ? { ...p, toolUses: [...p.toolUses, { id: data.id, name: data.tool, input: data.input }] }
              : p,
          );
        } else if (evt.type === 'tool_result') {
          const data = evt.data as { tool: string; result: unknown; id: string };
          setPending((p) =>
            p
              ? {
                  ...p,
                  toolUses: p.toolUses.map((t) =>
                    t.id === data.id ? { ...t, result: data.result } : t,
                  ),
                }
              : p,
          );
          if (data.tool === 'update_brief') {
            qc.invalidateQueries({ queryKey: ['instance-by-slug'] });
            qc.invalidateQueries({ queryKey: ['instances'] });
            qc.invalidateQueries({ queryKey: ['tree'] });
          }
          if (data.tool === 'submit_fal_job') {
            // Acabamos de encolar una generación → mostrar el banner "procesando"
            void refetchActiveJobs();
            void refetchFailedBatch();
          }
        } else if (evt.type === 'message_complete') {
          const data = evt.data as { cost: number };
          setStreamingCost((c) => c + data.cost);
        } else if (evt.type === 'error') {
          const data = evt.data as { message: string };
          setLastError(data.message);
          console.error('LLM error:', data.message);
        }
      });
    } catch (err) {
      setLastError((err as Error).message);
      console.error(err);
    } finally {
      setStreaming(false);
      setPending(null);
      setStreamingCost(0);
      // Esperar el refetch para que `messages` traiga el user msg + assistant
      // antes de quitar el optimistic — evita parpadeo entre estados.
      await refetch();
      setPendingUser(null);
      void refetchCost();
      qc.invalidateQueries({ queryKey: ['chat', chatId, 'assets'] });
    }
  };

  const send = async () => {
    const allAttachmentIds = Array.from(new Set([...upload.completedAssetIds, ...extraAssetIds]));
    if ((!draft.trim() && allAttachmentIds.length === 0) || streaming) return;
    if (!upload.allDone) return;
    const text = draft;
    setDraft('');
    setExtraAssetIds([]);
    upload.clear();
    setPendingUser({
      text: text || '(sin texto)',
      attachmentsCount: allAttachmentIds.length,
      createdAt: new Date().toISOString(),
    });
    await runStream(`/chats/${chatId}/messages`, {
      text: text || '(sin texto)',
      attachments: allAttachmentIds,
    });
  };

  const retry = async () => {
    if (streaming) return;
    await runStream(`/chats/${chatId}/retry`, {});
  };

  // Puente ReferenceTray → chat: cuando el operador confirma una corrección
  // (chips del gate anti-repetición), el tray publica el mensaje acá y lo
  // mandamos solo, adjuntando el output que se está corrigiendo.
  useEffect(() => {
    if (!bridgePending || bridgePending.chatId !== chatId || streaming) return;
    const { text, attachmentIds } = bridgePending;
    clearBridge();
    setPendingUser({
      text,
      attachmentsCount: attachmentIds.length,
      createdAt: new Date().toISOString(),
    });
    void runStream(`/chats/${chatId}/messages`, { text, attachments: attachmentIds });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bridgePending, chatId, streaming]);

  // Mostrar el botón Reintentar si:
  //  (a) no está streameando ahora
  //  (b) el último mensaje persistido es user (el agente no contestó)
  //  (c) ya hay al menos un mensaje (no es un chat nuevo)
  const lastMsg = messages?.[messages.length - 1];
  const canRetry = !streaming && !!lastMsg && lastMsg.role === 'user';

  return (
    <div
      className="flex flex-col h-full relative"
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDrop={onDrop}
    >
      {/* Overlay de drop activo */}
      {dragOver && (
        <div className="absolute inset-0 z-20 bg-vision-dark/40 border-4 border-dashed border-vision rounded-lg flex items-center justify-center pointer-events-none">
          <div className="text-vision text-xl font-medium">
            📎 Soltá para adjuntar al chat
          </div>
        </div>
      )}

      <ChatHeader
        chat={chat}
        totalCost={totalCost}
        llmCost={(persistedCost?.llm ?? 0) + streamingCost}
        falCost={persistedCost?.fal ?? 0}
      />

      {activeJobs && activeJobs.length > 0 && <ProcessingBanner count={activeJobs.length} />}

      <div
        ref={scrollRef}
        className="flex-1 overflow-y-auto p-4 space-y-4 bg-[radial-gradient(circle_at_top,rgba(15,23,42,0.4),rgba(2,6,23,0.9))]"
      >
        {messages
          ?.filter((m) => {
            // Ocultar los pasos intermedios persistidos de un turno cortado por el
            // tope: mensajes rol `tool` y assistants que son solo tool_use (sin
            // texto). Son contexto para el modelo, no burbujas para el usuario.
            if (m.role !== 'user' && m.role !== 'assistant') return false;
            if (m.role === 'assistant' && !extractText(m.content).trim()) return false;
            return true;
          })
          .map((m) => (
            <MessageBubble key={m.id} message={m} agentSlug={chat?.agentSlug} />
          ))}
        {pendingUser && (
          <div className="flex justify-end">
            <div className="max-w-[78%] rounded-2xl rounded-tr-sm bg-vision-dark/80 text-slate-50 px-3 py-2 shadow-sm opacity-90">
              <div className="whitespace-pre-wrap break-words text-sm">{pendingUser.text}</div>
              {pendingUser.attachmentsCount > 0 && (
                <div className="mt-1 text-[11px] text-slate-200/70">
                  📎 {pendingUser.attachmentsCount} adjunto
                  {pendingUser.attachmentsCount === 1 ? '' : 's'}
                </div>
              )}
              <div className="text-[10px] text-slate-200/60 text-right mt-0.5 flex items-center justify-end gap-1">
                <span>
                  {new Date(pendingUser.createdAt).toLocaleTimeString('es-AR', {
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </span>
                <span className="text-slate-300/70">✓</span>
              </div>
            </div>
          </div>
        )}
        {pending &&
          (pending.text || pending.toolUses.length > 0 ? (
            <AssistantBubble agentSlug={chat?.agentSlug} text={pending.text} toolUses={pending.toolUses} streaming />
          ) : (
            <TypingIndicator agentSlug={chat?.agentSlug} />
          ))}
        {canRetry && (
          <div className="flex justify-center pt-1">
            <div className="flex flex-col items-center gap-1">
              {lastError && (
                /* `whitespace-pre-line` + alineado a la izquierda: el backend
                   manda un texto de varias líneas que explica CUÁL cuenta falló
                   (Anthropic o fal.ai) y qué hacer. Centrado y en una línea era
                   ilegible, y por eso el equipo terminaba mirando el JSON crudo. */
                <div className="text-xs text-red-200 bg-red-950/40 border border-red-900 px-3 py-2 rounded-lg max-w-lg text-left whitespace-pre-line leading-relaxed">
                  {lastError}
                </div>
              )}
              <button
                onClick={() => void retry()}
                className="text-xs px-3 py-1.5 rounded-full bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 flex items-center gap-1.5"
                title="El agente no respondió. Reintentar el último mensaje."
              >
                🔄 Reintentar respuesta
              </button>
            </div>
          </div>
        )}
        {/* Banner de jobs fallidos: solo aparece si NO hay activos del mismo
            mensaje (eso significaría que ya se reintentó). Botón único que
            re-encola TODOS los jobs failed con un solo click. */}
        {failedBatch && (!activeJobs || activeJobs.length === 0) && (
          <FailedJobsBanner
            chatId={chatId}
            batch={failedBatch}
            isRetrying={retryBatch.isPending || streaming}
            onRetry={async () => {
              const result = await retryBatch.mutateAsync({
                chatId,
                messageId: failedBatch.messageId,
              });
              void refetchActiveJobs();
              void refetchFailedBatch();
              // Si el fallo es de validación, el backend YA posteó un user msg
              // con el error explícito; ahora hay que arrancar el LLM para que
              // corrija. Si fue transitorio, el job ya está reencolado y no
              // hace falta gastar tokens.
              if (result.mode === 'needs-llm') {
                await refetch();
                await retry();
              }
            }}
          />
        )}
      </div>

      {/* Composer */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void send();
        }}
        className="border-t border-slate-800 bg-slate-900 p-3 space-y-2"
      >
        {/* Chips de attachments pendientes/listos */}
        {upload.attachments.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {upload.attachments.map((a) => (
              <AttachmentChip key={a.id} att={a} onRemove={() => upload.remove(a.id)} />
            ))}
          </div>
        )}

        <div className="relative">
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={onTextareaChange}
            onPaste={onPaste}
            onKeyDown={(e) => {
              if (mention && e.key === 'Escape') {
                e.preventDefault();
                setMention(null);
                return;
              }
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                e.preventDefault();
                void send();
              }
            }}
            placeholder={
              typeof window !== 'undefined' && window.innerWidth < 640
                ? 'Mensaje… (# para refs)'
                : 'Mensaje (Cmd/Ctrl+Enter envía). Drop/Cmd+V para imágenes. Escribí # para mencionar referencias.'
            }
            className="w-full bg-slate-950 border border-slate-700 rounded-lg p-3 resize-none text-slate-100 focus:outline-none focus:border-vision"
            rows={3}
            disabled={streaming}
          />

          {mention && filteredAssets.length > 0 && (
            <div className="absolute bottom-full left-0 right-0 mb-1 bg-slate-900 border border-slate-700 rounded-lg shadow-xl max-h-64 overflow-y-auto z-30">
              <div className="px-2 py-1 text-[10px] text-slate-500 border-b border-slate-800">
                Referencias del proyecto · esc para cerrar
              </div>
              {filteredAssets.slice(0, 8).map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => insertMention(a)}
                  className="w-full flex items-center gap-2 px-2 py-2 hover:bg-slate-800 text-left"
                >
                  {a.mimeType.startsWith('image/') && (
                    <img
                      src={a.accessUrl ?? ''}
                      alt=""
                      className="w-10 h-10 object-cover rounded"
                    />
                  )}
                  <div className="flex-1 min-w-0 text-sm">
                    <div className="font-mono text-vision">#{a.metadata?.shortRef ?? a.id.slice(0, 8)}</div>
                    {a.metadata?.description && (
                      <div className="text-xs text-slate-400 truncate">{a.metadata.description}</div>
                    )}
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="px-3 py-1.5 text-sm rounded bg-slate-800 hover:bg-slate-700 text-slate-300"
              disabled={streaming}
              title="Adjuntar archivo"
            >
              📎 Adjuntar
            </button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept="image/*,video/*,audio/*"
              onChange={onFilePicker}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => setRestyleOpen(true)}
              className="px-3 py-1.5 text-sm rounded bg-slate-800 hover:bg-slate-700 text-slate-300"
              disabled={streaming}
              title="Reestilizar esta 360 (elegir estilo, animar, 4K)"
            >
              🎨 Reestilizar
            </button>
            {!upload.allDone && (
              <span className="text-xs text-slate-500">Subiendo adjuntos…</span>
            )}
          </div>
          <button
            type="submit"
            className="px-4 py-2 bg-vision-dark hover:bg-vision rounded-lg disabled:opacity-50"
            disabled={
              streaming ||
              !upload.allDone ||
              (!draft.trim() && upload.completedAssetIds.length === 0)
            }
          >
            {streaming ? 'Generando…' : 'Enviar'}
          </button>
        </div>
      </form>

      {restyleOpen && (
        <Restyle360Panel
          onClose={() => setRestyleOpen(false)}
          onConfirm={(orden) => {
            setDraft(orden);
            setRestyleOpen(false);
            setTimeout(() => textareaRef.current?.focus(), 0);
          }}
        />
      )}
    </div>
  );
}

function AttachmentChip({
  att,
  onRemove,
}: {
  att: PendingAttachment;
  onRemove: () => void;
}) {
  const isImage = att.mimeType.startsWith('image/');
  return (
    <div
      className={`relative group rounded-lg border overflow-hidden ${
        att.status === 'error'
          ? 'border-red-500/60 bg-red-950/30'
          : att.status === 'uploading'
            ? 'border-slate-700 bg-slate-800/50 animate-pulse'
            : 'border-vision-dark/60 bg-slate-800'
      }`}
      title={att.error ?? `${att.name} · ${formatSize(att.sizeBytes)}`}
    >
      {isImage ? (
        <img src={att.previewUrl} alt={att.name} className="w-20 h-20 object-cover" />
      ) : (
        <div className="w-20 h-20 flex items-center justify-center text-2xl text-slate-400">
          📄
        </div>
      )}
      <button
        type="button"
        onClick={onRemove}
        className="absolute top-0.5 right-0.5 bg-slate-950/80 hover:bg-red-700 text-slate-200 rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity"
        title="Quitar"
      >
        ×
      </button>
      {att.status === 'uploading' && (
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-vision animate-pulse" />
      )}
      {att.status === 'error' && (
        <div className="absolute bottom-0 left-0 right-0 text-xs text-red-300 bg-red-950/80 px-1 truncate">
          ⚠ {att.error?.slice(0, 20)}
        </div>
      )}
    </div>
  );
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatTime(iso: string | Date): string {
  const d = typeof iso === 'string' ? new Date(iso) : iso;
  return d.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
}

function agentEmoji(slug: string | undefined): string {
  if (!slug) return '✨';
  if (slug.includes('360')) return '🌐';
  if (slug.includes('image')) return '🖼';
  if (slug.includes('video')) return '🎬';
  if (slug.includes('audio')) return '🎵';
  if (slug.includes('avatar')) return '🗣';
  if (slug.includes('3d')) return '🧊';
  if (slug.includes('cost')) return '💰';
  if (slug.includes('prompt')) return '✍️';
  if (slug.includes('pipeline') || slug.includes('orchestrator')) return '🛠';
  return '✨';
}

function MessageBubble({ message, agentSlug }: { message: Message; agentSlug?: string }) {
  const isUser = message.role === 'user';
  const text = extractText(message.content);
  const time = formatTime(message.createdAt);
  if (isUser) {
    return (
      <div className="flex justify-end">
        <div className="max-w-[78%] rounded-2xl rounded-tr-sm bg-vision-dark/80 text-slate-50 px-3 py-2 shadow-sm">
          <div className="whitespace-pre-wrap break-words text-sm">{text}</div>
          {message.attachments && message.attachments.length > 0 && (
            <div className="mt-1 text-[11px] text-slate-200/70">
              📎 {message.attachments.length} adjunto{message.attachments.length === 1 ? '' : 's'}
            </div>
          )}
          <div className="text-[10px] text-slate-200/60 text-right mt-0.5 flex items-center justify-end gap-1">
            <span>{time}</span>
            <span className="text-sky-300">✓✓</span>
          </div>
        </div>
      </div>
    );
  }
  // Assistant
  const slug = message.agentSlug ?? agentSlug;
  // Resultados de una generación, para mostrar inline (batch summary del backend).
  const results = Array.isArray((message.metadata as { results?: unknown })?.results)
    ? ((message.metadata as { results: InlineResult[] }).results)
    : [];
  return (
    <AssistantBubble
      agentSlug={slug}
      text={text}
      time={time}
      attachmentsCount={message.attachments?.length ?? 0}
      results={results}
    />
  );
}

interface InlineResult {
  id: string;
  mimeType: string;
  shortRef?: string | null;
}

/** Grilla de resultados (imagen/video) renderizada inline en la burbuja del agente. */
function InlineResults({ results }: { results: InlineResult[] }) {
  if (!results.length) return null;
  return (
    <div className="mt-2 grid grid-cols-2 gap-2">
      {results.map((r) => {
        const isVideo = r.mimeType.startsWith('video/');
        const proxy = `${BASE}/assets/${r.id}/proxy`;
        return (
          <div key={r.id} className="bg-slate-950 border border-slate-700 rounded-lg overflow-hidden">
            {isVideo ? (
              <video src={proxy} controls className="w-full h-auto max-h-48 bg-black" />
            ) : (
              <a href={proxy} target="_blank" rel="noreferrer" title="Abrir en grande">
                <img src={`${BASE}/assets/${r.id}/thumb`} alt="" className="w-full h-32 object-cover hover:opacity-90" />
              </a>
            )}
            {r.shortRef && (
              <div className="px-2 py-1 text-[10px] font-mono text-vision">#{r.shortRef}</div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function AssistantBubble({
  agentSlug,
  text,
  time,
  attachmentsCount = 0,
  toolUses = [],
  streaming = false,
  results = [],
}: {
  agentSlug?: string;
  text: string;
  time?: string;
  attachmentsCount?: number;
  toolUses?: { id: string; name: string; input: unknown; result?: unknown }[];
  streaming?: boolean;
  results?: InlineResult[];
}) {
  return (
    <div className="flex justify-start items-end gap-2">
      <div className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-sm shrink-0">
        {agentEmoji(agentSlug)}
      </div>
      <div className="max-w-[78%] rounded-2xl rounded-tl-sm bg-slate-800 text-slate-100 px-3 py-2 shadow-sm">
        {text && <MarkdownText text={text} />}
        {streaming && !text && <DotsAnimation />}
        {results.length > 0 && <InlineResults results={results} />}
        {attachmentsCount > 0 && (
          <div className="mt-1 text-[11px] text-slate-400">
            📎 {attachmentsCount} adjunto{attachmentsCount === 1 ? '' : 's'}
          </div>
        )}
        {toolUses.length > 0 && (
          <div className="mt-2 space-y-1">
            {toolUses.map((t) => {
              const jobId =
                t.name === 'render_spot' &&
                t.result &&
                typeof t.result === 'object' &&
                'jobId' in t.result
                  ? String((t.result as { jobId: unknown }).jobId)
                  : null;
              return (
                <div key={t.id}>
                  <div className="text-[11px] bg-slate-950/60 border border-slate-700 rounded px-2 py-1 text-slate-400">
                    <span className="text-vision">🔧 {t.name}</span>
                    {t.result !== undefined && <span className="ml-2 text-emerald-400">✓</span>}
                  </div>
                  {jobId && <RenderJobCard jobId={jobId} />}
                </div>
              );
            })}
          </div>
        )}
        {time && (
          <div className="text-[10px] text-slate-400 text-right mt-0.5">{time}</div>
        )}
      </div>
    </div>
  );
}

function TypingIndicator({ agentSlug }: { agentSlug?: string }) {
  return (
    <div className="flex justify-start items-end gap-2">
      <div className="w-7 h-7 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center text-sm shrink-0">
        {agentEmoji(agentSlug)}
      </div>
      <div className="rounded-2xl rounded-tl-sm bg-slate-800 px-4 py-3 shadow-sm">
        <DotsAnimation />
      </div>
    </div>
  );
}

function DotsAnimation() {
  return (
    <div className="flex items-center gap-1" aria-label="Escribiendo">
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-[bounce_1s_-0.3s_infinite]" />
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-[bounce_1s_-0.15s_infinite]" />
      <span className="w-1.5 h-1.5 rounded-full bg-slate-400 animate-[bounce_1s_infinite]" />
    </div>
  );
}

/**
 * Render markdown del agente: tablas (gfm), bold, listas, code, links, etc.
 * Estilizado para encajar adentro de la burbuja sin colores estridentes.
 */
function MarkdownText({ text }: { text: string }) {
  return (
    <div className="text-sm text-slate-100 max-w-none break-words">
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          p: ({ children }) => <p className="my-1.5 first:mt-0 last:mb-0">{children}</p>,
          ul: ({ children }) => <ul className="list-disc pl-5 my-1.5 space-y-0.5">{children}</ul>,
          ol: ({ children }) => <ol className="list-decimal pl-5 my-1.5 space-y-0.5">{children}</ol>,
          li: ({ children }) => <li className="leading-snug">{children}</li>,
          strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
          em: ({ children }) => <em className="italic text-slate-200">{children}</em>,
          code: ({ children, className }) => {
            const isBlock = (className ?? '').includes('language-');
            if (isBlock) {
              return (
                <pre className="bg-slate-950 border border-slate-700 rounded p-2 my-2 overflow-x-auto text-xs">
                  <code className={className}>{children}</code>
                </pre>
              );
            }
            return (
              <code className="bg-slate-950 px-1 py-0.5 rounded text-[12px] text-vision font-mono">
                {children}
              </code>
            );
          },
          h1: ({ children }) => <h1 className="text-base font-semibold mt-2 mb-1">{children}</h1>,
          h2: ({ children }) => <h2 className="text-sm font-semibold mt-2 mb-1">{children}</h2>,
          h3: ({ children }) => <h3 className="text-sm font-semibold mt-1.5 mb-1">{children}</h3>,
          a: ({ children, href }) => (
            <a
              href={href}
              target="_blank"
              rel="noreferrer"
              className="text-vision underline hover:text-vision/80"
            >
              {children}
            </a>
          ),
          hr: () => <hr className="my-2 border-slate-700" />,
          blockquote: ({ children }) => (
            <blockquote className="border-l-2 border-vision-dark pl-2 my-1.5 text-slate-300 italic">
              {children}
            </blockquote>
          ),
          table: ({ children }) => (
            <div className="my-2 overflow-x-auto rounded border border-slate-700">
              <table className="w-full text-[12px] border-collapse">{children}</table>
            </div>
          ),
          thead: ({ children }) => <thead className="bg-slate-900">{children}</thead>,
          tbody: ({ children }) => <tbody>{children}</tbody>,
          tr: ({ children }) => (
            <tr className="border-b border-slate-700 last:border-b-0">{children}</tr>
          ),
          th: ({ children }) => (
            <th className="text-left font-semibold text-slate-200 px-2 py-1 border-r border-slate-700 last:border-r-0">
              {children}
            </th>
          ),
          td: ({ children }) => (
            <td className="px-2 py-1 align-top border-r border-slate-700 last:border-r-0">
              {children}
            </td>
          ),
        }}
      >
        {text}
      </ReactMarkdown>
    </div>
  );
}

/**
 * Banner persistente arriba del scroll: hay N jobs en fal.ai todavía corriendo.
 * El operador puede salir del chat — al volver el banner sigue ahí (la query
 * polea cada 8s y el SSE job.completed/failed lo refresca al toque).
 */
/**
 * Traduce el error técnico de fal.ai a algo que el operador entienda.
 * El mensaje crudo queda al final entre paréntesis para debugging.
 */
function humanizeFalError(raw: string | null): string {
  if (!raw) return 'fal.ai devolvió un error sin detalle.';

  // El backend ya redactó un mensaje humano y accionable (sin saldo / fal
  // caído). Lo mostramos tal cual: re-interpretarlo acá por keywords lo
  // rompía — el mensaje de "fal caído" contiene la palabra "saldo" y caía en
  // la rama equivocada, avisando de falta de plata cuando no era eso.
  if (raw.startsWith('⚠️') || raw.startsWith('🔧')) return raw;

  const r = raw.toLowerCase();
  if (r.includes('not found') || r.includes('404') || r.includes('no existe en el catálogo') || r.includes('slug inválido')) {
    return 'El asistente usó un endpoint que no existe en fal.ai (slug inválido). Reintentar NO lo arregla — escribile al asistente para que regenere con el modelo correcto.';
  }
  if (r.includes('unprocessable') || r.includes('422')) {
    const said = falSaid(raw);
    // Si fal explicó el motivo, se muestra TEXTUAL. Antes acá había una lista
    // de causas probables inventadas por nosotros ("imágenes inaccesibles,
    // formato no soportado…") que mandaba al operador a buscar donde no era.
    return said
      ? `fal.ai rechazó el pedido. Esto fue lo que contestó, textual: «${said}»`
      : 'fal.ai rechazó el pedido y no dijo por qué. Los parámetros habían pasado ' +
          'nuestra validación, así que el rechazo vino del modelo — probá reformulando ' +
          'el prompt o cambiando la imagen de referencia.';
  }
  if (r.includes('rate limit') || r.includes('429')) {
    return 'Demasiadas peticiones a fal.ai. Esperá 1-2 minutos antes de reintentar.';
  }
  if (r.includes('timeout') || r.includes('etimedout')) {
    return 'fal.ai tardó demasiado en responder. Suele ser temporal — reintentar funciona casi siempre.';
  }
  if (r.includes('500') || r.includes('502') || r.includes('503') || r.includes('internal server')) {
    return 'Error temporal del servidor de fal.ai. Reintentar en unos segundos suele funcionar.';
  }
  if (
    r.includes('insufficient') ||
    r.includes('balance') ||
    r.includes('quota') ||
    r.includes('saldo') ||
    r.includes('recarg') ||
    r.includes('exhausted') ||
    r.includes('add funds') ||
    r.includes('payment required') ||
    r.includes('402')
  ) {
    // Nombra la cuenta y aclara cuál NO es: son dos saldos separados y
    // confundirlos hizo que se recargara la que no era (2026-09-04).
    return (
      'No hay saldo en la cuenta de fal.ai — la que genera las imágenes y los videos. ' +
      'No es un error tuyo. Ojo: Anthropic y fal.ai son cuentas distintas con saldos separados; ' +
      'la que hay que recargar acá es fal.ai. Avisale al admin y, apenas haya saldo, reintentá.'
    );
  }
  const said = falSaid(raw);
  return said
    ? `fal.ai devolvió un error. Esto fue lo que contestó, textual: «${said}»`
    : 'fal.ai devolvió un error inesperado. Mirá el detalle técnico acá abajo.';
}

/**
 * Devuelve lo que fal.ai explicó TEXTUALMENTE, si el backend lo pudo capturar
 * del cuerpo de la respuesta (`explainFalError` lo pega después de este
 * marcador). Null cuando fal no mandó detalle — en ese caso decimos que no
 * sabemos, nunca una causa probable inventada.
 */
function falSaid(raw: string): string | null {
  const marker = 'fal.ai dijo:';
  const i = raw.indexOf(marker);
  if (i === -1) return null;
  return raw.slice(i + marker.length).trim() || null;
}

function FailedJobsBanner({
  batch,
  isRetrying,
  onRetry,
}: {
  chatId: string;
  batch: FailedBatch;
  isRetrying: boolean;
  onRetry: () => Promise<void> | void;
}) {
  const n = batch.jobs.length;
  // Tomamos el primer error como representativo (suele ser el mismo para todos
  // los jobs del mismo turno: misma config, mismo bug).
  const firstError = batch.jobs[0]?.errorMessage ?? null;
  const explained = humanizeFalError(firstError);
  return (
    <div className="flex justify-start items-end gap-2 pt-1">
      <div className="w-7 h-7 rounded-full bg-red-950 border border-red-900 flex items-center justify-center text-sm shrink-0">
        ⚠️
      </div>
      <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-red-950/40 border border-red-900/60 text-slate-100 px-3 py-2 shadow-sm space-y-2">
        <div className="text-sm">
          <strong className="text-red-300">
            {n === 1 ? 'La generación falló' : `Las ${n} generaciones fallaron`}
          </strong>
        </div>
        <div className="text-[13px] text-slate-200">{explained}</div>
        {firstError && (
          <details className="text-[11px] text-slate-400">
            <summary className="cursor-pointer hover:text-slate-300">Detalle técnico</summary>
            <pre className="mt-1 whitespace-pre-wrap break-words bg-slate-950/60 p-1.5 rounded">
              {firstError}
            </pre>
          </details>
        )}
        <button
          onClick={() => void onRetry()}
          disabled={isRetrying}
          className="px-3 py-1.5 text-xs rounded-full bg-vision-dark hover:bg-vision text-white disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          {isRetrying
            ? '⏳ Reencolando…'
            : `🔄 Reintentar ${n === 1 ? 'la generación' : `las ${n}`}`}
        </button>
      </div>
    </div>
  );
}

function ProcessingBanner({ count }: { count: number }) {
  return (
    <div className="border-y border-vision-dark/40 bg-vision-dark/15 px-4 py-2 flex items-center gap-3">
      <div className="relative w-5 h-5 shrink-0">
        <div className="absolute inset-0 rounded-full border-2 border-vision/30" />
        <div className="absolute inset-0 rounded-full border-2 border-transparent border-t-vision animate-spin" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-sm text-slate-100 font-medium">
          🎨 Procesando {count} {count === 1 ? 'generación' : 'generaciones'} en fal.ai
        </div>
        <div className="text-[11px] text-slate-400">
          Podés cerrar esta sesión — los resultados quedan guardados y aparecen acá cuando vuelvas.
        </div>
      </div>
    </div>
  );
}

function extractText(content: unknown): string {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    return content
      .filter((b): b is { type: 'text'; text: string } => (b as { type?: string }).type === 'text')
      .map((b) => b.text)
      .join('\n');
  }
  return JSON.stringify(content);
}
